import React from 'react';
import { Button } from '@material-ui/core';
import { Control, useForm, DeepMap, FieldError } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import {
  AppDatePicker,
  AppSelect,
  AppTextField,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { Translations } from '../../configuration';
import NavigationConfig from 'navigation/config';
import { useHistory } from 'react-router';
import * as DateUtils from 'utils/dates';
import * as VatReturnSelectors from 'redux/reducers/vatReturn_list_reducer/selectors';
import { StateStatus } from 'redux/utils/common';
import useQuery from 'custom_hooks/useQuery';

interface FormFields {
  vatNumber: string | null;
  period: string | null;
  remindedDateFrom: Date | null;
  remindedDateTo: Date | null;
}

interface IProps {
  vatNumber: string | null;
  period: string | null;
  remindedDateFrom: Date | null;
  remindedDateTo: Date | null;
}

const FilterForm = (props: IProps) => {
  const history = useHistory();
  const queryParams = useQuery();
  const defaultValues = {
    vatNumber: props.vatNumber ? props.vatNumber : null,
    period: props.period ? props.period : null,
    remindedDateFrom: props.remindedDateFrom ? props.remindedDateFrom : null,
    remindedDateTo: props.remindedDateTo ? props.remindedDateTo : null,
  };
  const { control, errors, handleSubmit, reset } = useForm<FormFields>({
    defaultValues: defaultValues,
  });

  React.useEffect(() => {
    reset(defaultValues);
  }, [
    props.vatNumber,
    props.period,
    props.remindedDateFrom,
    props.remindedDateTo,
    reset,
  ]);

  const onSubmit = (data: FormFields) => {
    const nextParams = new URLSearchParams(queryParams.toString());
    nextParams.delete('vatNumber');
    nextParams.delete('period');
    nextParams.delete('remindedDateFrom');
    nextParams.delete('remindedDateTo');
    nextParams.delete('sortField');
    nextParams.delete('page');
    nextParams.delete('pageSize');
    nextParams.delete('order');

    if (data.vatNumber) nextParams.set('vatNumber', data.vatNumber);
    if (data.period) nextParams.set('period', data.period);
    if (data.remindedDateFrom)
      nextParams.set(
        'remindedDateFrom',
        DateUtils.DateInQueryFormat(data.remindedDateFrom)
      );
    if (data.remindedDateTo)
      nextParams.set(
        'remindedDateTo',
        DateUtils.DateInQueryFormat(data.remindedDateTo)
      );

    const queryString = nextParams.toString();
    history.push(
      NavigationConfig.notificationsPage().path +
        (queryString ? `?${queryString}` : '')
    );
  };

  const clearFilter = () => {
    reset({
      vatNumber: null,
      period: null,
      remindedDateFrom: null,
      remindedDateTo: null,
    });
    const nextParams = new URLSearchParams(queryParams.toString());
    nextParams.delete('vatNumber');
    nextParams.delete('period');
    nextParams.delete('remindedDateFrom');
    nextParams.delete('remindedDateTo');
    nextParams.delete('sortField');
    nextParams.delete('page');
    nextParams.delete('pageSize');
    nextParams.delete('order');
    const queryString = nextParams.toString();
    history.push(
      NavigationConfig.notificationsPage().path +
        (queryString ? `?${queryString}` : '')
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ marginBottom: '0.6rem' }}>
      <GridLayout
        elements={
          [
            {
              element: renderVatNumberField(errors, control),
              size: 3,
              id: 'vatNumber',
            },
            {
              element: renderPeriodField(errors, control),
              size: 3,
              id: 'period',
            },
            {
              element: renderRemindedDateFromField(errors, control),
              size: 2,
              id: 'remindedDateFrom',
            },
            {
              element: renderRemindedDateToField(errors, control),
              size: 2,
              id: 'remindedDateTo',
            },
            {
              element: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{
                      width: '47%',
                      marginTop: '0.6rem',
                      marginRight: '0.25rem',
                    }}
                  >
                    <TranslatedText textMap={Translations.search} />
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{
                      width: '47%',
                      marginTop: '0.6rem',
                      marginLeft: '0.25rem',
                    }}
                    onClick={clearFilter}
                  >
                    <TranslatedText textMap={Translations.clear} />
                  </Button>
                </div>
              ),
              size: 2,
              id: 'actions',
            },
          ] as any
        }
      />
    </form>
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      id="vatNumber"
      label={
        <TranslatedText textMap={Translations.vat_number_search_control} />
      }
      error={'vatNumber' in errors}
      name="vatNumber"
      control={control}
      rules={{}}
      helperText={errors.vatNumber && errors.vatNumber.message}
    />
  );
};

const renderPeriodField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  const quarters = VatReturnSelectors.useGetQuarters();
  const monthQuarters = VatReturnSelectors.useGetMonthQuarterList();
  let select_options: any[] = [
    {
      id: null,
      label: Translations.period_search_control.English,
    },
  ];
  const periodOptions = [];
  if (quarters && quarters.status === StateStatus.Success && quarters.data) {
    periodOptions.push(...quarters.data);
  }
  if (
    monthQuarters &&
    monthQuarters.status === StateStatus.Success &&
    monthQuarters.data
  ) {
    periodOptions.push(...monthQuarters.data);
  }

  const seen = new Set();
  for (let quarter of periodOptions) {
    if (seen.has(quarter.short_text)) continue;
    seen.add(quarter.short_text);
    select_options.push({
      id: quarter.short_text,
      label: quarter.short_text,
    });
  }

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={<TranslatedText textMap={Translations.period_search_control} />}
      id="period"
      name="period"
      error={'period' in errors}
      control={control}
      rules={{}}
      options={select_options}
      helperText={errors.period && errors.period.message}
    />
  );
};

const renderRemindedDateFromField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      id="remindedDateFrom"
      label={<TranslatedText textMap={Translations.reminded_date_from} />}
      error={'remindedDateFrom' in errors}
      name="remindedDateFrom"
      control={control}
      rules={{}}
      autoOk
    />
  );
};

const renderRemindedDateToField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      id="remindedDateTo"
      label={<TranslatedText textMap={Translations.reminded_date_to} />}
      error={'remindedDateTo' in errors}
      name="remindedDateTo"
      control={control}
      rules={{}}
      autoOk
    />
  );
};

export default FilterForm;
